import React, { useRef, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CategoryNavigationProps {
  categories: string[];
  selectedCategory: string | null;
  onCategorySelect: (category: string) => void;
  isCategoryFixed: boolean;
}

const CategoryNavigation: React.FC<CategoryNavigationProps> = ({
  categories,
  selectedCategory,
  onCategorySelect,
  isCategoryFixed,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(true);
  // Track when we're programmatically scrolling to avoid feedback loops
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  // Track user interactions - this helps distinguish between auto-scrolling and manual browsing
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  // Store previous selected category to detect changes
  const prevSelectedCategoryRef = useRef<string | null>(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      setIsUserScrolling(true);
      scrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
      // Reset user scrolling flag after animation completes
      setTimeout(() => setIsUserScrolling(false), 500);
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      setIsUserScrolling(true);
      scrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
      // Reset user scrolling flag after animation completes
      setTimeout(() => setIsUserScrolling(false), 500);
    }
  };

  // Check scroll position to determine if shadows should be shown
  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftShadow(scrollLeft > 10);
      setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      const handleScroll = () => {
        checkScrollPosition();
        if (!isProgrammaticScroll) {
          setIsUserScrolling(true);
          // Reset after a short delay
          clearTimeout(scrollContainer.dataset.scrollTimeout as any);
          scrollContainer.dataset.scrollTimeout = setTimeout(() => {
            setIsUserScrolling(false);
          }, 500) as any;
        }
      };

      scrollContainer.addEventListener("scroll", handleScroll);
      // Initial check
      checkScrollPosition();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [isProgrammaticScroll]);

  // Update shadows when categories or window size changes
  useEffect(() => {
    checkScrollPosition();
    window.addEventListener("resize", checkScrollPosition);
    return () => {
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, [categories]);

  // Modified effect to only scroll to selected category on explicit selection changes
  useEffect(() => {
    // Skip if no selected category or we're still in programmatic scrolling
    if (!selectedCategory || !scrollRef.current || isProgrammaticScroll) return;

    // Only scroll to make category visible if:
    // 1. The selected category actually changed (not just re-render)
    // 2. The user is not currently manually scrolling
    const hasSelectedCategoryChanged =
      prevSelectedCategoryRef.current !== selectedCategory;
    prevSelectedCategoryRef.current = selectedCategory;

    if (hasSelectedCategoryChanged && !isUserScrolling) {
      setIsProgrammaticScroll(true);

      const tabElement = scrollRef.current.querySelector(
        `[data-value="${selectedCategory}"]`,
      ) as HTMLElement;
      if (tabElement) {
        // Get scrollRef's dimensions for calculation
        const scrollContainer = scrollRef.current;
        const containerLeft = scrollContainer.getBoundingClientRect().left;
        const containerWidth = scrollContainer.clientWidth;
        const containerRight = containerLeft + containerWidth;

        // Get tab's position
        const tabRect = tabElement.getBoundingClientRect();

        // Check if tab is visible in the scroll container
        const isTabVisible =
          tabRect.left >= containerLeft - 20 &&
          tabRect.right <= containerRight + 20;

        if (!isTabVisible) {
          // Center the tab in the visible area
          tabElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }

      // Clear the programmatic scroll flag after animation completes
      setTimeout(() => {
        setIsProgrammaticScroll(false);
      }, 500);
    }
  }, [selectedCategory, isProgrammaticScroll, isUserScrolling]);

  return (
    <div
      className={cn(
        "w-full sticky z-30 bg-white",
        isCategoryFixed ? "fixed top-[55px] left-0 right-0 shadow-md" : "",
      )}
    >
      <div className="relative flex items-center max-w-screen-xl mx-auto px-2">
        {/* Tabs Container with Arrows Inside */}
        <div className="w-full flex items-center">
          {/* Left Arrow for Desktop View */}
          <div
            className={cn(
              "hidden lg:flex items-center justify-center h-full mr-2 z-10",
              showLeftShadow ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
          >
            <button
              onClick={scrollLeft}
              className="flex-shrink-0 w-9 h-9 rounded-full bg-white border-2 border-redCrimson text-redCrimson shadow-md flex items-center justify-center hover:bg-redCrimson hover:text-white transition-colors"
              aria-label="Scroll left"
              disabled={!showLeftShadow}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div
            ref={scrollRef}
            className="flex-grow overflow-x-auto scrollbar-none scroll-smooth"
          >
            <Tabs
              value={selectedCategory || undefined}
              onValueChange={(value) => onCategorySelect(value)}
              className="w-full"
            >
              <TabsList
                className={cn(
                  "h-auto bg-transparent flex w-full justify-start p-0 rounded-none",
                )}
              >
                {categories.map((category) => (
                  <TabsTrigger
                    key={category}
                    value={category}
                    data-value={category}
                    className={cn(
                      "px-4 py-3 rounded-none text-base font-semibold border-b-2 whitespace-nowrap transition-all duration-200",
                      "data-[state=active]:border-redCrimson data-[state=active]:text-redCrimson data-[state=active]:shadow-none",
                      "data-[state=inactive]:border-transparent",
                      "focus-visible:outline-redCrimson focus-visible:ring-0 focus-visible:ring-offset-0",
                    )}
                  >
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Right Arrow for Desktop View */}
          <div
            className={cn(
              "hidden lg:flex items-center justify-center h-full ml-2 z-10",
              showRightShadow ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
          >
            <button
              onClick={scrollRight}
              className="flex-shrink-0 w-9 h-9 rounded-full bg-white border-2 border-redCrimson text-redCrimson shadow-md flex items-center justify-center hover:bg-redCrimson hover:text-white transition-colors"
              aria-label="Scroll right"
              disabled={!showRightShadow}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryNavigation;
